import _ from "lodash";
import React, { useMemo, useState } from "react";
import classes from "./MessageActivityV2.scss";
import { connect } from "react-redux";
import { Trans } from "react-i18next";
import { Popover } from "@toddle-edu/react-tiny-popover";
import classNames from "classnames";
import { useDispatch } from "Hooks/useDispatch";
import UserAccessPoint from "MessagingV2/components/UserAccessPoint";

import {
  setMessagingSections,
  goToChatIdRoute,
} from "MessagingV2/modules/MessagingModule";

import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getUserType,
  getDefaultStudentNameOrderPreference,
  getUserId,
} from "modules/CommonSelectors";

import {
  CHANNEL_ACTIVITY_TYPES,
  CHANNEL_TYPES,
  getFormattedNameForUserType,
  getSubText,
  MESSAGING_SECTIONS,
} from "MessagingV2/modules/Utils";

import UserNameAvatar from "AppComponents/UserNameAvatar/UserNameAvatar";

import {
  UserName,
  AVATAR_RENDER_MODES,
  UserAvatarWithAccessPoint,
} from "MessagingV2/components/UserAvatar";
import { isIdEqual } from "Utils/idHelpers";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

//in this order we will display activity to users
const activityOrder = ["staff", "student", "parent"];

const styles = {
  avatarStyles: {
    size: "small",
    randomizeColor: true,
    shade: "light",
    shape: "square",
  },

  customSubTextStyle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    textWrap: "nowrap",
  },
};

const USER_TYPE_LOCALE = {
  staff: {
    key: "staff",
    locale: "common:staff_count_lowercase",
  },
  student: {
    key: "student",
    locale: "common:student_count",
  },
  parent: {
    key: "parent",
    locale: "common:family_member_count",
  },
};

export const getActivityText = ({ userGroupBy, index, t }) => {
  const key = _.keys(userGroupBy)[index];
  const value = _.values(userGroupBy)[index];
  const locale = USER_TYPE_LOCALE[key].locale;

  return {
    label: t(locale, {
      count: _.size(value),
    }),
    value,
  };
};

const UserDropdownPopover = ({
  t,
  options,
  defaultNameSortPreference,
  currentUserType,
}) => {
  return (
    <div className={classes.popoverRoot}>
      <div className={classes.popoverUserList}>
        {_.map(options, user => {
          const {
            id,
            name,
            value,
            profileImage,
            isArchived,
            type,
            alertTypes = [],
          } = user;
          return (
            <div className={classes.userItem} key={id}>
              <UserNameAvatar
                id={id}
                profileImage={profileImage}
                name={name}
                viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
                shouldDynamicColor={true}
                isArchived={isArchived}
                showAvatar={true}
                avatarStyles={styles.avatarStyles}
                showArchivedText={true}
                showTooltip={false}
                customSubTextStyle={styles.customSubTextStyle}
                customSubText={getSubText({
                  loggedInUserRole: currentUserType,
                  messagedUserObj: user,
                  messagedUserRole: type,
                  t,
                  defaultStudentNameSortPreference: defaultNameSortPreference,
                })}
                userNameAvatarWrapper={UserAccessPoint}
                userNameAvatarWrapperProps={{
                  ...user,
                  userType: type,
                }}
                studentAlertsConfig={{
                  studentId: id,
                  alertTypes,
                  showAlertPopover: false,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const userDropdownMapStateToProps = state => ({
  defaultNameSortPreference: getDefaultNameSortPreference(state),
  currentUserType: getUserType(state),
});

const UserDropdownPopoverContent = connect(userDropdownMapStateToProps)(
  UserDropdownPopover
);

const UsersDropdown = ({ options, children, t }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closePopover = () => setPopoverOpen(false);
  const openPopover = () => setPopoverOpen(true);

  const handleOpenPopoverKeyDown = e => onEnterOrSpaceKeyDown(e, openPopover);

  return (
    <Popover
      isOpen={popoverOpen}
      onClickOutside={closePopover}
      containerClassName={classes.popoverContainer}
      positions={["right", "left", "top", "bottom"]} // preferred positions by priority
      content={<UserDropdownPopoverContent t={t} options={options} />}
    >
      <span
        className={classNames("text-label-3", classes.link)}
        onClick={openPopover}
        role="button"
        tabIndex={0}
        onKeyDown={handleOpenPopoverKeyDown}
      >
        {children}
      </span>
    </Popover>
  );
};

const getUserListItems = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  return _.map(users, user => {
    const fullName = getFormattedNameForUserType({
      user: user,
      orderBy: defaultNameSortPreference,
      defaultStudentNameOrderPreference,
      isFormattedNameV2: true,
    });

    const { id } = user;
    return {
      key: id,
      name: fullName,
      ...user,
    };
  });
};

const getUserByType = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  const userGroupBy = _.groupBy(users, "type");
  const staff = _.get(userGroupBy, "staff", []);

  const student = _.get(userGroupBy, "student", []);

  const parent = _.get(userGroupBy, "parent", []);

  const userTypeOrderBy = _.reduce(
    activityOrder,
    (result, order) => {
      if (userGroupBy[order]) {
        result[order] = getUserListItems({
          users: _.get(userGroupBy, order, []),
          defaultNameSortPreference,
          defaultStudentNameOrderPreference,
        });
      }
      return result;
    },
    {}
  );

  return { staff, student, parent, userGroupBy: userTypeOrderBy };
};

const ActivityTextComponent = props => {
  const {
    targetUsers,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    createdBy,
    type = "ADD",
    activityTextClass,
  } = props;

  const {
    staff = [],
    student = [],
    parent = [],
    userGroupBy = {},
  } = getUserByType({
    users: targetUsers,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  });
  const targetUserSize = _.size(targetUsers);

  const ifSingleTargetUser = targetUserSize === 1;

  const { t } = useI18n(["common"]);

  //based on type we will pick locale
  //of whether to add member or remove member
  const DESCRIPTION_TYPE_LOCALE_MAP = {
    ADD: {
      JOINED_LEFT_CHANNEL: "common:has_joined_the_channel",
      MULTIPLE_JOINED_LEFT_CHANNEL: "common:has_joined_the_channel_with_others",
      ALL_TYPE_USERS_ADDED_REMOVED: "common:added_member_with_all_type",
      TWO_TYPE_USERS_ADDED_REMOVED: "common:added_member_with_two_type",
      ONE_TYPE_USER_ADDED_REMOVED: "common:added_member_with_one_type",
    },
    REMOVE: {
      JOINED_LEFT_CHANNEL: "common:has_removed_the_channel",
      MULTIPLE_JOINED_LEFT_CHANNEL: "common:has_left_the_channel_with_others",
      ALL_TYPE_USERS_ADDED_REMOVED: "common:removed_member_with_all_type",
      TWO_TYPE_USERS_ADDED_REMOVED: "common:removed_member_with_two_type",
      ONE_TYPE_USER_ADDED_REMOVED: "common:removed_member_with_one_type",
    },
  };

  //if user types are same and creator also included in target then message will be "Joined conversation"
  //else message will be <UserName> added <UserName>, below is logic for same
  const sameTargetUserType =
    _.size(staff) === targetUserSize ||
    _.size(student) === targetUserSize ||
    _.size(parent) === targetUserSize;

  const createdById = createdBy?.id;

  const targetUsersIds = useMemo(() => _.map(targetUsers, user => user.id), [
    targetUsers,
  ]);
  const isCreatorIncludeInTarget = useMemo(
    () => _.includes(targetUsersIds, createdById),
    [targetUsersIds, createdById]
  );
  const targetUserTypeCount = _.size(_.keys(userGroupBy));

  if (sameTargetUserType && isCreatorIncludeInTarget) {
    const { value } = getActivityText({ userGroupBy, index: 0, t });
    return ifSingleTargetUser ? (
      <div className={activityTextClass}>
        {t(DESCRIPTION_TYPE_LOCALE_MAP[type].JOINED_LEFT_CHANNEL)}
      </div>
    ) : (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].MULTIPLE_JOINED_LEFT_CHANNEL
          }
        >
          <UsersDropdown
            t={t}
            options={_.filter(value, user => !isIdEqual(user.id, createdById))}
          >
            {{ number: _.size(value) - 1 }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  if (targetUserTypeCount === 3) {
    return (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].ALL_TYPE_USERS_ADDED_REMOVED
          }
        >
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 0, t }).value}
            t={t}
          >
            {{
              count_1: getActivityText({ userGroupBy, index: 0, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 1, t }).value}
            t={t}
          >
            {{
              count_2: getActivityText({ userGroupBy, index: 1, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 2, t }).value}
            t={t}
          >
            {{
              count_3: getActivityText({ userGroupBy, index: 2, t }).label,
            }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  if (targetUserTypeCount === 2) {
    return (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].TWO_TYPE_USERS_ADDED_REMOVED
          }
        >
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 0, t }).value}
            t={t}
          >
            {{
              count_1: getActivityText({ userGroupBy, index: 0, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 1, t }).value}
            t={t}
          >
            {{
              count_2: getActivityText({ userGroupBy, index: 1, t }).label,
            }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  return (
    <div className={activityTextClass}>
      <Trans
        i18nKey={DESCRIPTION_TYPE_LOCALE_MAP[type].ONE_TYPE_USER_ADDED_REMOVED}
      >
        <UsersDropdown
          options={getActivityText({ userGroupBy, index: 0, t }).value}
          t={t}
        >
          {{ count_1: getActivityText({ userGroupBy, index: 0, t }).label }}
        </UsersDropdown>
      </Trans>
    </div>
  );
};

const MessageMovedToActivity = ({
  createdBy,
  defaultStudentNameOrderPreference,
  defaultNameSortPreference,
}) => {
  const { t } = useI18n(["common"]);

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  const activityTextClass = classNames(
    "text-body-2",
    classes.activityTextWithoutFlex
  );

  const id = _.get(createdBy, "id");

  const isCurrentUser = isIdEqual(getUserId(), _.get(createdBy, "id"));

  const options = [
    {
      key: id,
      name: fullName,
      value: id,
      ...createdBy,
    },
  ];

  return (
    <div className={activityTextClass}>
      <Trans i18nKey={"common:user_moved_messages_from"}>
        {isCurrentUser ? (
          <span>{{ user: t("common:you") }}</span>
        ) : (
          <UsersDropdown t={t} options={options}>
            {{ user: fullName }}
          </UsersDropdown>
        )}
      </Trans>
    </div>
  );
};

const MessageMovedFromActivity = ({
  createdBy,
  transferChannelLabel,
  transferChannelId,
  isAllMessagesMoved,
  defaultStudentNameOrderPreference,
  defaultNameSortPreference,
}) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  const activityTextBoldClass = classNames(
    "text-label-3",
    classes.activityTextBold,
    classes.link
  );

  const activityTextClass = classNames(
    "text-body-2",
    classes.activityTextWithoutFlex
  );

  const id = _.get(createdBy, "id");

  const options = [
    {
      key: id,
      name: fullName,
      value: id,
      ...createdBy,
    },
  ];

  const isCurrentUser = isIdEqual(getUserId(), _.get(createdBy, "id"));
  const onChannelClick = () => {
    dispatch(goToChatIdRoute({ chatId: transferChannelId }));
    dispatch(setMessagingSections({ section: MESSAGING_SECTIONS.CHANNELS }));
  };
  const handleChannelKeyDown = e => {
    onEnterOrSpaceKeyDown(e, onChannelClick);
  };

  return (
    <div className={activityTextClass}>
      <Trans
        i18nKey={
          isAllMessagesMoved
            ? "common:user_moved_all_messages_to"
            : "common:user_moved_messages_to"
        }
      >
        {isCurrentUser ? (
          <span>{{ user: t("common:you") }}</span>
        ) : (
          <UsersDropdown t={t} options={options}>
            {{ user: fullName }}
          </UsersDropdown>
        )}
        <span
          className={activityTextBoldClass}
          onClick={onChannelClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleChannelKeyDown}
        >
          {{
            label: transferChannelLabel,
          }}
        </span>
      </Trans>
    </div>
  );
};

/**
 * This code follows new UI revemp of activity
 */
const MessageActivityV2 = props => {
  const {
    message,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  } = props;

  const { t } = useI18n(["common"]);
  const activity = _.get(message, "channelActivity", {});
  const createdBy = _.get(message, "createdBy", {});
  const { type, after, targetUsers } = activity;

  const activityTextClass = classNames("text-body-2", classes.activityText);
  const activityTextWithoutFlex = classNames(
    "text-body-2",
    classes.activityTextWithoutFlex
  );
  const activityTextBoldClass = classNames(
    "text-label-3",
    classes.activityTextBold
  );

  switch (type) {
    case CHANNEL_ACTIVITY_TYPES.DESCRIPTION_CHANGE:
      return (
        <div className={activityTextWithoutFlex}>
          <Trans i18nKey={"common:updated_channel_desc_to_new_desc"}>
            <span className={activityTextBoldClass}>{{ label: after }}</span>
          </Trans>
        </div>
      );
    case CHANNEL_ACTIVITY_TYPES.BANNER_MESSAGE_CHANGE:
      return (
        <div className={activityTextWithoutFlex}>
          <Trans
            i18nKey={"common:updated_channel_banner_message_to_new_message"}
          >
            <span className={activityTextBoldClass}>{{ label: after }}</span>
          </Trans>
        </div>
      );

    case CHANNEL_ACTIVITY_TYPES.LABEL_CHANGE:
      return (
        <div className={activityTextWithoutFlex}>
          <Trans i18nKey={"common:updated_channel_name_to_new_name"}>
            <span className={activityTextBoldClass}>{{ label: after }}</span>
          </Trans>
        </div>
      );

    case CHANNEL_ACTIVITY_TYPES.USER_ADD: {
      return (
        <ActivityTextComponent
          createdBy={createdBy}
          targetUsers={targetUsers}
          defaultNameSortPreference={defaultNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
          activityTextClass={activityTextClass}
          type="ADD"
        />
      );
    }

    case CHANNEL_ACTIVITY_TYPES.USER_REMOVE:
      return (
        <ActivityTextComponent
          createdBy={createdBy}
          targetUsers={targetUsers}
          defaultNameSortPreference={defaultNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
          activityTextClass={activityTextClass}
          type="REMOVE"
        />
      );

    case CHANNEL_ACTIVITY_TYPES.MESSAGE_MOVE_TO:
      return (
        <MessageMovedToActivity
          createdBy={createdBy}
          defaultNameSortPreference={defaultNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
        />
      );

    case CHANNEL_ACTIVITY_TYPES.MESSAGE_MOVE_FROM: {
      const label = _.get(activity, "chat.label", "");
      const channelId = _.get(activity, "chat.id");
      const isAllMessagesMoved = _.get(activity, "isAllMessagesMoved", false);
      return (
        <MessageMovedFromActivity
          createdBy={createdBy}
          transferChannelLabel={label}
          transferChannelId={channelId}
          isAllMessagesMoved={isAllMessagesMoved}
          defaultNameSortPreference={defaultNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
        />
      );
    }

    case CHANNEL_ACTIVITY_TYPES.TYPE_CHANGE: {
      const before = _.get(activity, "before", "");
      const after = _.get(activity, "after", "");
      if (
        _.isEqual(before, CHANNEL_TYPES.CHANNEL_PUBLIC) &&
        _.isEqual(after, CHANNEL_TYPES.CHANNEL_CUSTOM)
      ) {
        return (
          <div className={activityTextWithoutFlex}>
            {t("common:channel_activity_updated_to_private")}
          </div>
        );
      }

      return (
        <div className={activityTextWithoutFlex}>
          {t("common:channel_activity_updated_to_public")}
        </div>
      );
    }
    default:
      return null;
  }
};

const MessageActivityWithDateV2 = props => {
  const {
    message,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isBlocked,
  } = props;

  const createdBy = _.get(message, "createdBy", {});

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  return (
    <div className={classes.activityContainerV2}>
      <UserAvatarWithAccessPoint
        isBlocked={isBlocked}
        user={createdBy}
        fullName={fullName}
        avatarRenderMode={AVATAR_RENDER_MODES.AVATAR}
      />
      <div className={classes.activityDetails}>
        <UserName
          user={createdBy}
          fullName={fullName}
          createdAt={message?.createdAt}
        />
        <MessageActivityV2
          message={message}
          defaultNameSortPreference={defaultNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
        />
      </div>
    </div>
  );
};

const activityMapStateToProps = state => {
  const defaultNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  return {
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  };
};

export default connect(activityMapStateToProps)(MessageActivityWithDateV2);
